import React, { useEffect, useState } from 'react';
import { Download, MonitorDown, ChevronDown } from 'lucide-react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

// Detect the user's current platform so the primary button points to the right download.
function detectPlatformKey(builds) {
  if (!builds.length) return null;
  const ua = (navigator.userAgent || '').toLowerCase();
  const platform = (navigator.platform || '').toLowerCase();
  const keys = builds.map((b) => b.platform_key);

  if (/android/.test(ua)) return keys.find((k) => k === 'android') || keys[0];
  if (/iphone|ipad|ipod/.test(ua)) return keys.find((k) => k === 'android') || keys[0]; // no iOS build yet
  if (/mac|darwin/.test(platform) || /mac os x/.test(ua)) {
    // Apple Silicon detection is imperfect from the browser; default to arm64 on modern Macs
    const arm = keys.find((k) => k === 'mac-arm64');
    return arm || keys.find((k) => k === 'mac-x64') || keys[0];
  }
  if (/win/.test(platform)) return keys.find((k) => k === 'windows') || keys[0];
  if (/linux/.test(platform)) {
    return keys.find((k) => k === 'linux-appimage') || keys[0];
  }
  return keys.find((k) => k === 'windows') || keys[0];
}

/**
 * DesktopDownloadButton
 * Shows a primary "Download for <your platform>" button with a chevron that reveals
 * all other platforms (Win / macOS x64+arm64 / Linux AppImage+tar.gz / Android APK).
 *
 * variant: "pill" (login page) | "inline" (sidebar)
 */
export const DesktopDownloadButton = ({ variant = 'pill', className = '' }) => {
  const [builds, setBuilds] = useState([]);
  const [primaryKey, setPrimaryKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/desktop/info`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.builds || [];
        setBuilds(list);
        setPrimaryKey(detectPlatformKey(list));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!builds.length) return null;

  const primary = builds.find((b) => b.platform_key === primaryKey) || builds[0];
  const others = builds.filter((b) => b.platform_key !== primary.platform_key);
  const hrefFor = (b) => `${API.replace(/\/api$/, '')}${b.download_url}`;

  if (variant === 'pill') {
    return (
      <div
        className={`inline-flex items-stretch rounded-full border border-border bg-secondary/60 overflow-hidden ${className}`}
        data-testid="desktop-download-pill"
      >
        <a
          href={hrefFor(primary)}
          download={primary.filename}
          data-testid="desktop-download-primary"
          className="flex items-center gap-2 pl-4 pr-3 py-2 hover:bg-secondary transition-colors text-sm text-slate-200"
        >
          <MonitorDown className="w-4 h-4 text-primary" />
          <span>Download for {primary.platform}</span>
          <span className="text-xs text-slate-500">{formatSize(primary.size_bytes)}</span>
        </a>
        {others.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger
              className="px-2 border-l border-border hover:bg-secondary transition-colors text-slate-400"
              data-testid="desktop-download-more"
              aria-label="Other platforms"
            >
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs text-slate-400">All platforms</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {builds.map((b) => (
                <DropdownMenuItem key={b.platform_key} asChild>
                  <a
                    href={hrefFor(b)}
                    download={b.filename}
                    data-testid={`desktop-download-item-${b.platform_key}`}
                    className="flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <Download className="w-3.5 h-3.5 text-primary" />
                      <span className="text-sm">{b.platform}</span>
                    </span>
                    <span className="text-xs text-slate-500">{formatSize(b.size_bytes)}</span>
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  // inline (sidebar)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`
          flex items-center justify-between gap-3 w-full px-3 py-2 rounded-lg
          bg-secondary/40 border border-border hover:bg-secondary hover:border-primary/40
          text-sm text-slate-300 transition-all duration-150
          ${className}
        `}
        data-testid="desktop-download-inline"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Download className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate">Get desktop / mobile app</span>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-64">
        <DropdownMenuLabel className="text-xs text-slate-400">Choose your platform</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {builds.map((b) => (
          <DropdownMenuItem key={b.platform_key} asChild>
            <a
              href={hrefFor(b)}
              download={b.filename}
              data-testid={`desktop-download-item-${b.platform_key}`}
              className="flex items-center justify-between gap-3 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <Download className="w-3.5 h-3.5 text-primary" />
                <span className="text-sm">{b.platform}</span>
              </span>
              <span className="text-xs text-slate-500">{formatSize(b.size_bytes)}</span>
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DesktopDownloadButton;
